/**
 * 通用资源 — 创建/批量上传共用的文件上传规则。
 * zip-only 类型与后端 Registry 同步（见 genericResourceZipPackageSpec.js 的 ZIP_ONLY_TYPES）。
 *
 * web(网页) 仍属「非资源包」类型（不要求 jsonConfig.url / zip），但基本信息左侧需上传封面图到 thumbUrl。
 */
import { isZipOnlyAssetType, ZIP_ONLY_TYPES } from './form/genericResourceZipPackageSpec'

/** 非文件型资源：走 schema 填 link 等字段，不要求 jsonConfig.url */
export const NON_FILE_TYPES = ['coupon(优惠券)', 'web(网页)', 'ai_assistant']

/** web 封面允许的本地扩展名（写入 thumbUrl，不占用 zip 下载链路的 jsonConfig.url） */
const WEB_COVER_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif']

export function isAiAssistantType(assetType) {
  return String(assetType || '') === 'ai_assistant'
}

/**
 * web 资源在基本信息左侧上传封面（thumbUrl），与「配置型」占位区分。
 * 为什么不复用 requiresFileUpload：那会强制 jsonConfig.url，info 页「下载资源」会误把封面当 zip。
 */
export function isWebCoverType(assetType) {
  return String(assetType || '') === 'web(网页)'
}

export { ZIP_ONLY_TYPES }

export function isNonFileType(assetType) {
  return NON_FILE_TYPES.includes(String(assetType || ''))
}

export function requiresFileUpload(assetType) {
  const type = String(assetType || '').trim()
  return type !== '' && !isNonFileType(type)
}

export function isZipOnlyType(assetType) {
  return isZipOnlyAssetType(assetType)
}

function hasWebCoverExtension(fileNameOrUrl) {
  const path = String(fileNameOrUrl || '').split('?')[0].toLowerCase()
  return WEB_COVER_EXTENSIONS.some((ext) => path.endsWith(ext))
}

/** 单个贴纸 zip 的产品上限，后端同样拦截；其它 zip 类型不限。 */
export const STICKER_ZIP_MAX_BYTES = 50 * 1024 * 1024

export function validateLocalFileSizeForAssetType(assetType, fileSize) {
  if (String(assetType || '') === 'stickers' && Number(fileSize) > STICKER_ZIP_MAX_BYTES) {
    return { ok: false, message: '单个贴纸 zip 不能超过 50MB，请检查素材' }
  }
  return { ok: true }
}

/**
 * 本地选文件前的格式与大小校验（批量上传、单条创建共用）。
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateLocalFileForAssetType(assetType, fileName, fileSize) {
  const sizeCheck = validateLocalFileSizeForAssetType(assetType, fileSize)
  if (!sizeCheck.ok) {
    return sizeCheck
  }
  if (isWebCoverType(assetType)) {
    if (!hasWebCoverExtension(fileName)) {
      return {
        ok: false,
        message: '封面图仅支持 jpg / jpeg / png / webp / gif',
      }
    }
    return { ok: true }
  }
  if (!requiresFileUpload(assetType)) {
    return { ok: true }
  }
  if (isZipOnlyType(assetType)) {
    const lower = String(fileName || '').toLowerCase()
    if (!lower.endsWith('.zip')) {
      return {
        ok: false,
        message: `${assetType} 类型仅支持 .zip 格式`,
      }
    }
  }
  return { ok: true }
}

/**
 * 拖拽上传只接受一个文件。多个文件明确提示，不悄悄取第一个；拖入的不是文件（如一段文字）时 message 为空，调用方直接忽略。
 * @returns {{ ok: boolean, file?: File, message?: string }}
 */
export function pickDroppedFile(fileList) {
  const files = Array.from(fileList || [])
  if (files.length === 0) {
    return { ok: false, message: '' }
  }
  if (files.length > 1) {
    return { ok: false, message: '一次只能拖入一个文件' }
  }
  return { ok: true, file: files[0] }
}

/**
 * 上传完成后的 URL 校验（应对 CDN 返回带 query 的地址）。
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateUploadedUrlForAssetType(assetType, url) {
  if (isWebCoverType(assetType)) {
    // 本地选文件已校验扩展名；CDN 返回地址偶发无后缀，这里只要求非空，避免误杀
    if (!url) {
      return { ok: false, message: '请上传封面图' }
    }
    return { ok: true }
  }
  if (!requiresFileUpload(assetType)) {
    return { ok: true }
  }
  if (!url) {
    return { ok: false, message: '请上传资源文件' }
  }
  if (isZipOnlyType(assetType)) {
    const path = String(url).split('?')[0].toLowerCase()
    if (!path.endsWith('.zip')) {
      return {
        ok: false,
        message: `${assetType} 类型仅支持 .zip 格式`,
      }
    }
  }
  return { ok: true }
}

/** Element UI 表单 rules：按资源类型动态生成 jsonConfig.url 校验 */
export function buildResourceUrlFormRules(assetType) {
  if (!requiresFileUpload(assetType)) {
    return {}
  }
  const rules = [
    { required: true, message: '请上传资源文件', trigger: 'manual' },
  ]
  if (isZipOnlyType(assetType)) {
    rules.push({
      validator(rule, value, callback) {
        const result = validateUploadedUrlForAssetType(assetType, value)
        if (result.ok) {
          callback()
        } else {
          callback(new Error(result.message))
        }
      },
      trigger: 'manual',
    })
  }
  return { 'jsonConfig.url': rules }
}

/** web 封面：校验 thumbUrl（基本信息左侧），不占用 jsonConfig.url */
export function buildWebCoverFormRules(assetType) {
  if (!isWebCoverType(assetType)) {
    return {}
  }
  return {
    thumbUrl: [
      { required: true, message: '请上传封面图', trigger: 'manual' },
    ],
  }
}
